import React, { createContext, useContext, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { TokenPayload } from '@wsfull/shared'

type AuthContextValue = {
  user: TokenPayload | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

function parseJwt(token: string): TokenPayload | null {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload))
  } catch {
    return null
  }
}

function getUserFromStorage(): TokenPayload | null {
  const token = localStorage.getItem('access_token')
  if (!token) return null
  const payload = parseJwt(token)
  if (!payload) return null
  // Check expiry
  const exp = (payload as any).exp
  if (exp && Date.now() / 1000 > exp) {
    localStorage.removeItem('access_token')
    return null
  }
  return payload
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<TokenPayload | null>(getUserFromStorage)

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<{ accessToken: string; refreshToken: string }>(
      '/auth/login',
      { email, password },
    )
    localStorage.setItem('access_token', data.accessToken)
    localStorage.setItem('refresh_token', data.refreshToken)
    setUser(parseJwt(data.accessToken))
  }, [])

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      api.post('/auth/logout', { refreshToken }).catch(() => null)
    }
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
